// @flow

import React, { useState } from 'react'
import { injectIntl } from 'react-intl'
import { colors } from '@app/themes'
import { extractContactFromResponse, maskPhoneNumber } from '@app/utils/phoneNumberMaskingUtils'
import { revealContactApi } from '@app/services/api/revealContact/revealContactApi'
import { T } from '@nv/rc/Components'
import { PhoneNumberContainer, RevealButton } from './styles'
import { showNotification } from '@app/utils/notify'
import { NOTIFICATIONS } from '@app/utils/constants'

/**
 * MaskedPhoneNumber component that displays masked phone numbers
 * with "Click to reveal (tracked)" functionality
 *
 * @param {string} phoneNumber - The full phone number to mask
 * @param {string} orderId - The order/tracking ID for API call
 * @param {string} country - The country code
 * @param {string} dataTestId - Optional test ID
 * @param {boolean} showCountryCode - Whether to show country code with revealed number
 */
const MaskedPhoneNumber = props => {
  const { phoneNumber, orderId, country, dataTestId = 'masked-phone-number', intl } = props
  const [isRevealed, setIsRevealed] = useState(false)
  const [revealedNumber, setRevealedNumber] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const handleReveal = async () => {
    if (isRevealed || isLoading) return

    setIsLoading(true)
    setError(false)

    try {
      const response = await revealContactApi(country, orderId)

      if (!response.ok || response.problem) {
        setError(true)
        const errorMessage = response?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_contact' })
        showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
        return
      }

      const fullContact = extractContactFromResponse(response)

      if (fullContact && fullContact.length > 0) {
        setRevealedNumber(fullContact)
        setIsRevealed(true)

        const warningMessage = intl.formatMessage({ id: 'contact_revealed_warning' })
        showNotification(warningMessage, NOTIFICATIONS.ERROR, 5000)
      } else {
        setError(true)
        const errorMessage = response?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_contact' })
        showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
      }
    } catch (err) {
      setError(true)
      const errorMessage = err?.data?.error?.message || intl.formatMessage({ id: 'unable_to_reveal_contact' })
      showNotification(errorMessage, NOTIFICATIONS.ERROR, 5000)
    } finally {
      setIsLoading(false)
    }
  }

  const maskedDigits = maskPhoneNumber(phoneNumber)

  if (isRevealed && revealedNumber) {
    return <span data-testid={`${dataTestId}-revealed`}>{revealedNumber}</span>
  }

  if (error) {
    return (
      <PhoneNumberContainer data-testid={`${dataTestId}-error`}>
        <span style={{ color: colors.salmon }}>
          <T id='unable_to_reveal_contact' />
        </span>
        {maskedDigits && <span data-testid={`${dataTestId}-masked-digits`}> {maskedDigits}</span>}
      </PhoneNumberContainer>
    )
  }

  return (
    <PhoneNumberContainer data-testid={dataTestId}>
      <RevealButton onClick={handleReveal} data-testid={`${dataTestId}-reveal-link`}>
        {isLoading ? <T id='loading' /> : <T id='click_to_reveal_tracked' />}
      </RevealButton>
      {maskedDigits && <span data-testid={`${dataTestId}-masked-digits`}>{maskedDigits}</span>}
    </PhoneNumberContainer>
  )
}

const MaskedPhoneNumberWithIntl = injectIntl(MaskedPhoneNumber)

export { MaskedPhoneNumberWithIntl as MaskedPhoneNumber }
export default MaskedPhoneNumberWithIntl
